import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { apiError } from "../lib/api";
import { computeFrame, stageBounds, stageIndexAt, totalDuration, displayName, simplifyPath, pathLength, playerPosAt, assignStandardPositions, POSITION_SLOTS, getStartingLineup, migratePlayCoordinates } from "../lib/engine";
import Whiteboard from "../components/Whiteboard";
import { DeletePlayDialog } from "../components/DeletePlayDialog";
import OverheadPitch from "../components/OverheadPitch";
import PlayerView from "../components/PlayerView";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Slider } from "../components/ui/slider";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Sheet, SheetContent } from "../components/ui/sheet";
import { toast } from "sonner";
import {
  ArrowLeft, Play, Pause, RotateCcw, Save, Share2,
  MousePointer2, Footprints, Send, Rocket, Hand, UserPlus, Shield, Cone,
  Type, Undo2, Redo2, Trash2, Eye, PanelLeftClose, PanelRightClose,
  Layout, Grid3x3, User, Plus, X, ChevronsRight, CircleDashed, Layers, StepForward, Crosshair, SlidersHorizontal, LayoutGrid, Presentation,
} from "lucide-react";

const uid = () => Math.random().toString(36).slice(2, 9);
const round1 = (n) => Math.round(n * 10) / 10;
const DEFAULT_STAGE_SECS = 3;
const withStages = (p, stages) => ({ ...p, stages, duration: stages.reduce((a, s) => a + s.duration, 0) });

const TOOLS = [
  { id: "select", label: "Select / Move", short: "Select", icon: MousePointer2 },
  { id: "run", label: "Run path", short: "Run", icon: Footprints },
  { id: "pass", label: "Hand pass", short: "Hand", icon: Hand },
  { id: "kick", label: "Kick pass", short: "Kick", icon: Send },
  { id: "kickout", label: "Kickout / Long ball", short: "Kickout", icon: Rocket },
  { id: "zone", label: "Space zone", short: "Space", icon: CircleDashed },
  { id: "addA", label: "Add home player", short: "Home", icon: UserPlus },
  { id: "addB", label: "Add opposition", short: "Opp", icon: Shield },
  { id: "cone", label: "Cone", short: "Cone", icon: Cone },
  { id: "text", label: "Text label", short: "Text", icon: Type },
  { id: "delete", label: "Delete", short: "Erase", icon: Trash2 },
];

const TOOL_HINTS = {
  select: "Drag any player to reposition them.",
  run: "Drag a player to draw a run path.",
  pass: "Tap the passer, then tap the receiver.",
  kick: "Tap the kicker, then tap the target player.",
  kickout: "Tap the keeper, then tap the target player.",
  zone: "Tap the pitch to drop a space zone — it fades as a player runs in.",
  addA: "Tap the pitch to add a home player.",
  addB: "Tap the pitch to add an opposition player.",
  cone: "Tap the pitch to place a cone.",
  text: "Tap the pitch to add a text label.",
  delete: "Tap a player to remove them.",
};

const EVENT_COLORS = { move: "#F59E0B", ball: "#38BDF8", opp: "#EF4444" };
const SPEEDS = [0.25, 0.5, 1, 1.5, 2];
const MARKER_DELAY = 1;
const PACES = [
  { id: "jog", label: "Jog", speed: 3.2 },
  { id: "run", label: "Run", speed: 5.5 },
  { id: "sprint", label: "Sprint", speed: 7.8 },
];
const paceSpeed = (id) => (PACES.find((p) => p.id === id) || PACES[1]).speed;

function useIsMobile() {
  const [m, setM] = useState(() => window.matchMedia("(max-width: 767px)").matches);
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 767px)");
    const fn = (e) => setM(e.matches);
    mq.addEventListener("change", fn);
    return () => mq.removeEventListener("change", fn);
  }, []);
  return m;
}

export default function Studio() {
  const { playId } = useParams();
  const nav = useNavigate();
  const isMobile = useIsMobile();
  const [inspectorOpen, setInspectorOpen] = useState(false);
  const [markerFollow, setMarkerFollow] = useState(true);
  const [continuousRun, setContinuousRun] = useState(false);
  const [pace, setPace] = useState("run");
  const [board, setBoard] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [play, setPlay] = useState(null);
  const [team, setTeam] = useState(null);
  const [t, setT] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [speed, setSpeed] = useState(1);
  const [loop, setLoop] = useState(false);
  const [view, setView] = useState("coach");
  const [focusId, setFocusId] = useState("A1");
  const [tool, setTool] = useState("select");
  const [selectedId, setSelectedId] = useState(null);
  const [selectedEventId, setSelectedEventId] = useState(null);
  const [leftOpen, setLeftOpen] = useState(true);
  const [rightOpen, setRightOpen] = useState(true);
  const [saving, setSaving] = useState(false);
  const [alignmentPending, setAlignmentPending] = useState(false);
  const [ballSource, setBallSource] = useState(null);
  const [activeStage, setActiveStage] = useState(0);
  const [watchId, setWatchId] = useState(null);
  const [dragPreview, setDragPreview] = useState(null);
  const [banner, setBanner] = useState(null);
  const stopAtRef = useRef(null);
  const holdRef = useRef(null);
  const pauseAtStagesRef = useRef(false);
  const stagesRef = useRef([]);

  const pastRef = useRef([]);
  const futureRef = useRef([]);
  const dragState = useRef(null);
  const rafRef = useRef(null);
  const [, force] = useState(0);

  useEffect(() => {
    api.get(`/plays/${playId}`).then((r) => {
      const { play: p, corrected } = migratePlayCoordinates(r.data);
      if (!Array.isArray(p.stages) || !p.stages.length) p.stages = [{ id: "s1", name: "Stage 1", duration: p.duration || 6 }];
      setAlignmentPending(corrected);
      setPlay(withStages(p, p.stages));
      setT(0);
      setActiveStage(0);
      api.get(`/teams/${p.teamId}`).then((tr) => setTeam(tr.data)).catch(() => {});
    }).catch((e) => { toast.error(apiError(e)); nav("/teams"); });
  }, [playId, nav]);

  const duration = play ? totalDuration(play) : 6;
  const frame = useMemo(() => (play ? computeFrame(play, t) : null), [play, t]);
  const possessionIssue = useMemo(() => {
    if (!play) return null;
    let holder = play.ball?.holderId;
    let availableAt = 0;
    for (const event of [...play.events].filter((item) => item.kind === "ball").sort((a, b) => a.startTime - b.startTime)) {
      if (event.startTime < availableAt || event.playerId !== holder) return { id: event.id, message: event.startTime < availableAt ? "Two ball events overlap. Adjust their timing." : `At ${event.startTime.toFixed(1)}s, ${displayName(play, event.playerId)} needs possession before this ${event.ballType || "pass"}. Review the previous pass or this event.` };
      holder = event.targetPlayerId || null;
      availableAt = event.endTime;
    }
    return null;
  }, [play]);
  const teamColors = { primary: team?.colorPrimary || "#0EA5E9" };
  const stages = useMemo(() => (play ? stageBounds(play) : []), [play]);
  stagesRef.current = stages;
  const stage = stages[Math.min(activeStage, stages.length - 1)] || stages[0];

  const seekTo = useCallback((v) => {
    setT(v);
    if (play) setActiveStage(stageIndexAt(play, v));
  }, [play]);

  useEffect(() => {
    if (!banner) return;
    const id = setTimeout(() => setBanner(null), 1600);
    return () => clearTimeout(id);
  }, [banner]);

  // Starting slots, not roster numbers, determine the jersey, name and position in every play.
  useEffect(() => {
    if (!team) return;
    const lineup = getStartingLineup(team);
    setPlay((prev) => {
      if (!prev) return prev;
      let changed = false;
      const entities = prev.entities.map((e) => {
        if (e.team !== "A") return e;
        const slotId = /^A([1-9]|1[0-5])$/.exec(e.id);
        if (!slotId) return e;
        const number = Number(slotId[1]);
        const slot = POSITION_SLOTS[number - 1];
        const sp = lineup[number - 1];
        const name = sp?.name || "";
        const role = sp?.role || slot.label;
        if (number !== e.number || name !== e.name || role !== e.role || slot.label !== e.position) {
          changed = true;
          return { ...e, number, name, role, position: slot.label };
        }
        return e;
      });
      return changed ? { ...prev, entities } : prev;
    });
  }, [team]); // eslint-disable-line react-hooks/exhaustive-deps

  // playback loop
  useEffect(() => {
    if (!playing) return;
    let last = performance.now();
    const loop2 = (now) => {
      if (holdRef.current && now < holdRef.current) { last = now; rafRef.current = requestAnimationFrame(loop2); return; }
      holdRef.current = null;
      const dt = (now - last) / 1000;
      last = now;
      setT((prev) => {
        let nt = prev + dt * speed;
        if (pauseAtStagesRef.current) {
          const next = stagesRef.current.find((s) => s.index > 0 && prev < s.start - 1e-6 && nt >= s.start - 1e-6);
          if (next) { holdRef.current = now + 750; setBanner({ name: next.name, index: next.index }); setActiveStage(next.index); return next.start; }
        }
        const stopAt = stopAtRef.current ?? duration;
        if (nt >= stopAt) {
          if (stopAtRef.current != null) { stopAtRef.current = null; setPlaying(false); return stopAt; }
          if (loop) return 0;
          setPlaying(false);
          return duration;
        }
        return nt;
      });
      rafRef.current = requestAnimationFrame(loop2);
    };
    rafRef.current = requestAnimationFrame(loop2);
    return () => cancelAnimationFrame(rafRef.current);
  }, [playing, speed, duration, loop]);

  // history
  const snapshot = useCallback(() => JSON.parse(JSON.stringify(play)), [play]);
  const pushPast = useCallback((snap) => { pastRef.current.push(snap); if (pastRef.current.length > 50) pastRef.current.shift(); futureRef.current = []; force((x) => x + 1); }, []);
  const commit = (mutator) => { pushPast(snapshot()); setPlay((p) => mutator(p)); };
  const undo = useCallback(() => {
    if (!pastRef.current.length) return;
    futureRef.current.push(JSON.parse(JSON.stringify(play)));
    setPlay(pastRef.current.pop());
    force((x) => x + 1);
  }, [play]);
  const redo = useCallback(() => {
    if (!futureRef.current.length) return;
    pastRef.current.push(JSON.parse(JSON.stringify(play)));
    setPlay(futureRef.current.pop());
    force((x) => x + 1);
  }, [play]);

  // player drag / run creation. In stage 1 the Select tool repositions the starting shape;
  // in later stages (or with the Run tool) a drag becomes a movement inside the active stage.
  const runLike = tool === "run" || (tool === "select" && activeStage > 0);
  // Event start: the playhead if it sits inside the active stage, otherwise the stage start.
  const eventStart = () => (stage ? round1(t >= stage.start - 1e-6 && t <= stage.end - 0.5 ? t : stage.start) : round1(t));
  // Extend a stage (no rescaling of its events) so a new movement fits at a realistic speed.
  const extendStage = (p, s, needEnd) => {
    if (!s || needEnd <= s.end + 1e-6) return p;
    const nd = Math.ceil((needEnd - s.start) * 2) / 2;
    const delta = nd - s.duration;
    return withStages({
      ...p,
      events: p.events.map((ev) => (ev.startTime >= s.end - 1e-6 ? { ...ev, startTime: round1(ev.startTime + delta), endTime: round1(ev.endTime + delta) } : ev)),
    }, p.stages.map((x, j) => (j === s.index ? { ...x, duration: nd } : x)));
  };
  // Continuous run: keep stage boundaries where they are so later stages overlap the run;
  // only grow the LAST stage if the run spills past the whole play.
  const extendLastStageTo = (p, needEnd) => {
    const total = p.stages.reduce((a, s) => a + s.duration, 0);
    if (needEnd <= total + 1e-6) return p;
    const add = Math.ceil((needEnd - total) * 2) / 2;
    const li = p.stages.length - 1;
    return withStages(p, p.stages.map((x, j) => (j === li ? { ...x, duration: round1(x.duration + add) } : x)));
  };

  const onMovePlayer = (id, world, isEnd) => {
    if (isEnd) {
      const ds = dragState.current;
      dragState.current = null;
      setDragPreview(null);
      if (!ds || !ds.moved) return;
      if (ds.runLike) {
        const st = eventStart();
        const startPos = frame.entities.find((e) => e.id === ds.id) || ds.orig;
        const pts = simplifyPath([{ x: startPos.x, y: startPos.y }, ...ds.points, ds.last], 1.2).slice(1);
        const waypoints = (pts.length ? pts : [ds.last]).slice(-12).map((p) => ({ x: round1(p.x), y: round1(p.y) }));
        const dist = pathLength([startPos, ...waypoints]);
        const runDur = Math.max(0.8, Math.min(12, round1(dist / paceSpeed(pace))));
        const en = round1(st + runDur);
        const runEv = { id: uid(), kind: "move", type: tool === "run" ? "PLAYER RUN" : "MOVE", playerId: ds.id, startTime: st, endTime: en, waypoints, pace, continuous: continuousRun };
        const newEvents = [runEv];
        const isHome = String(ds.id).startsWith("A");
        let markerName = null;
        let needEnd = en;
        if (markerFollow && isHome) {
          const marker = frame.entities
            .filter((e) => e.team === "B")
            .map((e) => ({ e, d: Math.hypot(e.x - startPos.x, e.y - startPos.y) }))
            .filter((o) => o.d <= 10)
            .sort((a, b) => a.d - b.d)[0];
          if (marker) {
            const rel = { x: marker.e.x - startPos.x, y: marker.e.y - startPos.y };
            const len = Math.hypot(rel.x, rel.y) || 1;
            const k = Math.min(len, 2.5) / len;
            const off = { x: rel.x * k, y: rel.y * k };
            const mStart = round1(st + MARKER_DELAY);
            const mEnd = round1(Math.max(en, mStart + Math.max(0.8, runDur * 0.9)));
            needEnd = Math.max(needEnd, mEnd);
            newEvents.push({
              id: uid(), kind: "move", type: "MARK", playerId: marker.e.id, followsId: ds.id,
              startTime: mStart, endTime: mEnd,
              waypoints: waypoints.map((w) => ({ x: round1(Math.max(1, Math.min(89, w.x + off.x))), y: round1(Math.max(1, Math.min(144, w.y + off.y))) })),
            });
            markerName = displayName(play, marker.e.id);
          }
        }
        const extended = stage && needEnd > stage.end + 1e-6;
        commit((p) => {
          const q = continuousRun ? extendLastStageTo(p, needEnd) : extendStage(p, stage, needEnd);
          return { ...q, events: [...q.events, ...newEvents] };
        });
        toast.success(`${tool === "run" ? "Run" : "Move"} added to ${stage?.name || "play"} · ${runDur.toFixed(1)}s${continuousRun ? " · continuous (overlaps later stages)" : ""}${markerName ? ` · ${markerName} follows ${MARKER_DELAY}s later` : ""}${!continuousRun && extended ? " · stage extended" : ""}`);
      }
      force((x) => x + 1);
      return;
    }
    if (world == null) return;
    if (!dragState.current) {
      const ent = play.entities.find((e) => e.id === id);
      if (!ent) return;
      if (!runLike) pushPast(snapshot());
      dragState.current = { id, orig: { x: ent.x, y: ent.y }, last: world, moved: false, runLike, points: [] };
    }
    const ds = dragState.current;
    if (ds.runLike) {
      const lastPt = ds.points[ds.points.length - 1] || ds.last;
      if (Math.hypot(world.x - lastPt.x, world.y - lastPt.y) >= 1.5) ds.points.push({ x: world.x, y: world.y });
    }
    ds.last = world;
    ds.moved = true;
    if (ds.runLike) setDragPreview({ id, x: world.x, y: world.y, points: [...ds.points, world] });
    else setPlay((p) => ({ ...p, entities: p.entities.map((e) => (e.id === id ? { ...e, x: round1(world.x), y: round1(world.y) } : e)) }));
  };

  // drag an existing waypoint handle of a run to reshape its curve
  const wpDrag = useRef(false);
  const onMoveWaypoint = (evId, idx, world, isEnd) => {
    if (isEnd) { wpDrag.current = false; return; }
    if (!wpDrag.current) { pushPast(snapshot()); wpDrag.current = true; }
    setPlay((p) => ({
      ...p,
      events: p.events.map((e) => (e.id === evId ? { ...e, waypoints: e.waypoints.map((w, i) => (i === idx ? { x: round1(world.x), y: round1(world.y) } : w)) } : e)),
    }));
  };

  const onSelect = (id) => {
    if (["pass", "kick", "kickout"].includes(tool)) {
      if (!ballSource) { setBallSource(id); toast.message("Now tap the target player"); }
      else { addBallEvent(ballSource, id, tool); setBallSource(null); }
      return;
    }
    if (tool === "delete") { deleteEntity(id); return; }
    setSelectedId(id);
    setSelectedEventId(null);
  };

  const addBallEvent = (from, target, kind) => {
    if (from === target) { toast.error("Pick a different target"); return; }
    const st = eventStart();
    const ballType = kind === "pass" ? "handpass" : kind === "kick" ? "kick" : "kickout";
    const a = frame.entities.find((e) => e.id === from), b = frame.entities.find((e) => e.id === target);
    const dist = a && b ? Math.hypot(a.x - b.x, a.y - b.y) : 20;
    const speed = kind === "kickout" ? 24 : kind === "kick" ? 20 : 10;
    const dur = Math.max(kind === "pass" ? 0.4 : 0.6, Math.min(3, round1(dist / speed)));
    const en = round1(st + dur);
    commit((p) => {
      const q = extendStage(p, stage, en);
      return {
        ...q,
        ball: q.events.some((e) => e.kind === "ball") ? q.ball : { ...q.ball, holderId: from },
        events: [...q.events, { id: uid(), kind: "ball", type: ballType.toUpperCase(), ballType, playerId: from, targetPlayerId: target, startTime: st, endTime: en }],
      };
    });
    toast.success(`${ballType} added to ${stage?.name || "play"} · ${dur.toFixed(1)}s`);
  };

  // Snap every player back to the standard GAA starting shape (opposition keeper in the far goal).
  const resetFormation = () => {
    commit((p) => {
      const pos = assignStandardPositions(p.entities);
      return { ...p, entities: p.entities.map((e) => (pos[e.id] ? { ...e, ...pos[e.id] } : e)) };
    });
    setT(0);
    setActiveStage(0);
    setPlaying(false);
    toast.success("Formation reset to standard starting positions");
  };

  // ---- stages ----
  // Add a stage. If the playhead sits inside an existing stage, SPLIT it there so new stages
  // can sit "over" an ongoing continuous run (reactions overlap it). Otherwise append a new stage.
  const addStage = () => {
    const idx = stageIndexAt(play, t);
    const s = stages[idx];
    const canSplit = s && t > s.start + 0.3 && t < s.end - 0.3;
    if (canSplit) {
      const firstDur = round1(t - s.start);
      const secondDur = round1(s.end - t);
      commit((p) => withStages(p, [
        ...p.stages.slice(0, idx),
        { ...p.stages[idx], duration: firstDur },
        { id: uid(), name: `${s.name} (cont.)`, duration: secondDur },
        ...p.stages.slice(idx + 1),
      ]));
      setActiveStage(idx + 1);
      setT(t);
      setPlaying(false);
      toast.success(`Stage split at ${t.toFixed(1)}s — add moves here that overlap the ongoing run`);
      return;
    }
    const n = stages.length + 1;
    const newStart = duration;
    commit((p) => withStages(p, [...p.stages, { id: uid(), name: `Stage ${n}`, duration: DEFAULT_STAGE_SECS }]));
    setActiveStage(n - 1);
    setT(newStart);
    setPlaying(false);
    toast.success(`Stage ${n} added — players start where Stage ${n - 1} left them`);
  };
  const selectStage = (i) => { setActiveStage(i); setT(stages[i].start); setPlaying(false); setSelectedEventId(null); };
  const playStage = (i = activeStage) => {
    const s = stages[i];
    if (!s) return;
    pauseAtStagesRef.current = false;
    setActiveStage(i);
    setT(s.start);
    stopAtRef.current = s.end;
    setBanner({ name: s.name, index: i });
    setPlaying(true);
  };
  const playAll = () => {
    if (!stages.length) return;
    stopAtRef.current = null;
    pauseAtStagesRef.current = true;
    setActiveStage(0);
    setT(0);
    setBanner({ name: stages[0].name, index: 0 });
    setPlaying(true);
  };
  const updateStage = (i, patch) => {
    if (patch.duration != null) {
      const s = stages[i];
      const nd = Math.max(1, patch.duration);
      const ratio = nd / s.duration, delta = nd - s.duration;
      commit((p) => withStages({
        ...p,
        events: p.events.map((ev) => {
          if (ev.startTime >= s.end - 1e-6) return { ...ev, startTime: round1(ev.startTime + delta), endTime: round1(ev.endTime + delta) };
          if (ev.startTime >= s.start - 1e-6) {
            const ns = s.start + (ev.startTime - s.start) * ratio;
            const ne = ev.endTime <= s.end + 1e-6 ? s.start + (ev.endTime - s.start) * ratio : ev.endTime + delta;
            return { ...ev, startTime: round1(ns), endTime: round1(Math.max(ns + 0.1, ne)) };
          }
          return ev;
        }),
      }, p.stages.map((x, j) => (j === i ? { ...x, duration: nd } : x))));
    } else {
      setPlay((p) => withStages(p, p.stages.map((x, j) => (j === i ? { ...x, ...patch } : x))));
    }
  };
  const deleteStage = (i) => {
    if (stages.length <= 1) { toast.error("A play needs at least one stage"); return; }
    const s = stages[i];
    commit((p) => withStages({
      ...p,
      events: p.events
        .filter((ev) => !(ev.startTime >= s.start - 1e-6 && ev.startTime < s.end - 1e-6))
        .map((ev) => (ev.startTime >= s.end - 1e-6 ? { ...ev, startTime: round1(ev.startTime - s.duration), endTime: round1(ev.endTime - s.duration) } : ev)),
    }, p.stages.filter((_, j) => j !== i)));
    const ni = Math.max(0, i - 1);
    setActiveStage(ni);
    setT(stages[ni].start);
    setPlaying(false);
    toast.success(`${s.name} removed`);
  };

  const onPitchPoint = (world) => {
    if (tool === "addA" || tool === "addB") {
      const teamKey = tool === "addA" ? "A" : "B";
      commit((p) => {
        const nums = p.entities.filter((e) => e.team === teamKey).map((e) => e.number);
        const number = (Math.max(0, ...nums) || 0) + 1;
        return { ...p, entities: [...p.entities, { id: teamKey + number + uid().slice(0, 3), team: teamKey, number, name: teamKey === "A" ? "New" : "Opp", position: "Player", role: "Player", x: round1(world.x), y: round1(world.y) }] };
      });
    } else if (tool === "zone") {
      commit((p) => ({ ...p, annotations: [...(p.annotations || []), { id: uid(), type: "zone", x: round1(world.x), y: round1(world.y), r: 12, team: "A" }] }));
      toast.success("Space zone added");
    } else if (tool === "cone") {
      commit((p) => ({ ...p, annotations: [...(p.annotations || []), { id: uid(), type: "cone", x: round1(world.x), y: round1(world.y) }] }));
    } else if (tool === "text") {
      const label = window.prompt("Label text:");
      if (label) commit((p) => ({ ...p, annotations: [...(p.annotations || []), { id: uid(), type: "text", x: round1(world.x), y: round1(world.y), text: label }] }));
    }
  };

  const deleteEntity = (id) => {
    commit((p) => ({ ...p, entities: p.entities.filter((e) => e.id !== id), events: p.events.filter((e) => e.playerId !== id && e.targetPlayerId !== id) }));
    if (selectedId === id) setSelectedId(null);
  };

  const updateEntity = (id, patch) => commit((p) => ({ ...p, entities: p.entities.map((e) => (e.id === id ? { ...e, ...patch } : e)) }));
  const updateEvent = (id, patch) => setPlay((p) => ({ ...p, events: p.events.map((e) => (e.id === id ? { ...e, ...patch } : e)) }));
  // Re-time an existing run for a new pace (extends its stage if needed).
  const setEventPace = (ev, newPace) => {
    const ent = play.entities.find((e) => e.id === ev.playerId);
    if (!ent) return;
    const from = playerPosAt(ent, play.events.filter((e) => e.id !== ev.id), ev.startTime);
    const dist = pathLength([from, ...(ev.waypoints || [])]);
    const dur = Math.max(0.8, Math.min(12, round1(dist / paceSpeed(newPace))));
    const en = round1(ev.startTime + dur);
    const s = stages.find((x) => ev.startTime >= x.start - 1e-6 && ev.startTime < x.end - 1e-6);
    commit((p) => {
      const q = extendStage(p, s, en);
      return { ...q, events: q.events.map((e) => (e.id === ev.id ? { ...e, pace: newPace, endTime: en } : e)) };
    });
  };
  const deleteEvent = (id) => { commit((p) => ({ ...p, events: p.events.filter((e) => e.id !== id) })); setSelectedEventId(null); };
  const removeAnnotation = (id) => commit((p) => ({ ...p, annotations: (p.annotations || []).filter((a) => a.id !== id) }));
  const updateAnnotation = (id, patch) => setPlay((p) => ({ ...p, annotations: (p.annotations || []).map((a) => (a.id === id ? { ...a, ...patch } : a)) }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/plays/${playId}`, play);
      setAlignmentPending(false);
      toast.success("Play saved");
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  const deletePlay = async () => {
    setDeleting(true);
    try { await api.delete(`/plays/${playId}`); toast.success(`"${play.name}" deleted`); nav(`/team/${play.teamId}`); }
    catch (e) { toast.error(apiError(e)); setDeleting(false); }
  };

  const share = () => {
    navigator.clipboard.writeText(`${window.location.origin}/studio/${playId}`);
    toast.success("Shareable link copied");
  };

  // keyboard shortcuts
  const tRef = useRef(0);
  tRef.current = t;
  const togglePlay = useCallback(() => {
    stopAtRef.current = null;
    pauseAtStagesRef.current = false;
    holdRef.current = null;
    if (tRef.current >= duration) setT(0);
    setPlaying((p) => !p);
  }, [duration]);
  useEffect(() => {
    const onKey = (e) => {
      if (["INPUT", "TEXTAREA"].includes(e.target.tagName)) return;
      if (e.code === "Space") { e.preventDefault(); togglePlay(); }
      else if (e.key === "r") { setT(0); setActiveStage(0); setPlaying(false); }
      else if (e.key === "z" && !e.shiftKey) { e.preventDefault(); undo(); }
      else if ((e.key === "Z" || (e.key === "z" && e.shiftKey))) { e.preventDefault(); redo(); }
      else if (e.key === "1") setView("coach");
      else if (e.key === "2") setView("player");
      else if (e.key === "3") setView("split");
      else if (e.key === "4") setBoard(true);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [undo, redo, togglePlay]);

  if (!play || !frame) {
    return <div className="h-screen flex items-center justify-center bg-background font-mono2 text-primary text-sm animate-pulse">LOADING STUDIO…</div>;
  }

  const selectedEntity = play.entities.find((e) => e.id === selectedId);
  const selectedEvent = play.events.find((e) => e.id === selectedEventId);
  const homePlayers = play.entities.filter((e) => e.team === "A");
  const activeCarrier = frame.ball?.carrierId;
  const stageRange = stage ? { start: stage.start, end: stage.end } : null;
  const hint = tool === "select" && activeStage > 0 ? `Drag a player — they move during ${stage?.name}. Tap to select.` : TOOL_HINTS[tool];
  const pvProps = { frame, focusId, teamColors, sport: play.sport, annotations: play.annotations, watchId, onWatch: setWatchId, gender: team?.gender || "boys" };

  const inspectorBody = (
    <div className="flex-1 overflow-y-auto p-4 space-y-5" data-testid="inspector-body">
        {selectedEvent ? (
          <EventInspector ev={selectedEvent} play={play} duration={duration} updateEvent={updateEvent} deleteEvent={deleteEvent} onClose={() => setSelectedEventId(null)} onSetPace={setEventPace} />
        ) : selectedEntity ? (
          <EntityInspector ent={selectedEntity} updateEntity={updateEntity} deleteEntity={deleteEntity} onViewAs={() => { setFocusId(selectedEntity.id); setView("player"); }} />
        ) : (
          <div className="text-sm text-muted-foreground">Select a player or timeline event to edit. Use the <span className="text-foreground">Run</span> tool and drag a player to draw movement.</div>
        )}

        {/* Stage settings */}
        {stage && (
          <div className="space-y-2 pt-2 border-t border-border" data-testid="stage-settings">
            <div className="text-xs font-semibold uppercase tracking-widest text-muted-foreground flex items-center gap-1"><Layers className="h-3.5 w-3.5" />Stage {activeStage + 1} of {stages.length}</div>
            <Input data-testid="stage-name-input" value={stage.name} onChange={(e) => updateStage(activeStage, { name: e.target.value })} className="h-8 text-xs" />
            <div>
              <label className="text-[11px] text-muted-foreground">Length · {stage.duration.toFixed(1)}s <span className="opacity-60">({stage.start.toFixed(1)}–{stage.end.toFixed(1)}s)</span></label>
              <Slider data-testid="stage-duration-slider" value={[stage.duration]} min={1} max={15} step={0.5} onValueChange={(v) => updateStage(activeStage, { duration: v[0] })} className="mt-2" />
            </div>
            <div className="flex gap-1.5">
              <Button data-testid="stage-play-btn" size="sm" variant="secondary" className="flex-1 h-8" onClick={() => playStage(activeStage)}><StepForward className="h-3.5 w-3.5 mr-1" />Play stage</Button>
              <Button data-testid="stage-delete-btn" size="sm" variant="ghost" className="h-8 text-muted-foreground hover:text-destructive" onClick={() => deleteStage(activeStage)} disabled={stages.length <= 1}><Trash2 className="h-3.5 w-3.5" /></Button>
            </div>
          </div>
        )}

        {/* View As */}
        <div className="pt-2 border-t border-border">
          <div className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-2 flex items-center gap-1"><Eye className="h-3.5 w-3.5" />View as</div>
          <div className="grid grid-cols-3 gap-1">
            {homePlayers.map((p) => (
              <button key={p.id} data-testid={`view-as-${p.id}`} onClick={() => { setFocusId(p.id); setView("player"); }} title={p.name}
                className={`text-left text-[11px] px-1.5 py-1 rounded-md border truncate ${focusId === p.id ? "border-primary bg-primary/10 text-foreground" : "border-border text-muted-foreground hover:border-primary/40"}`}>
                <span className="font-mono2 font-bold text-primary">#{p.number}</span> {(p.name || "").split(" ").slice(-1)[0]}
              </button>
            ))}
          </div>
          <div className="mt-3">
            <label className="text-[11px] text-muted-foreground flex items-center gap-1"><Crosshair className="h-3 w-3" />Camera follows</label>
            <Select value={watchId || "free"} onValueChange={(v) => setWatchId(v === "free" ? null : v)}>
              <SelectTrigger data-testid="watch-select" className="h-8 mt-1 text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="free">Free look</SelectItem>
                <SelectItem value="ball">Ball</SelectItem>
                {play.entities.filter((e) => e.id !== focusId).map((e) => (
                  <SelectItem key={e.id} value={e.id}>{displayName(play, e.id)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Play settings */}
        <div className="space-y-3 pt-2 border-t border-border">
          <div className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">Play settings</div>
          <div className="text-[11px] text-muted-foreground">Total length <span className="font-mono2 text-foreground">{duration.toFixed(1)}s</span> across {stages.length} stage{stages.length === 1 ? "" : "s"}. Adjust stage lengths above.</div>
          <Button data-testid="reset-formation-btn" size="sm" variant="secondary" className="w-full h-8" onClick={resetFormation}><LayoutGrid className="h-3.5 w-3.5 mr-1" />Reset formation</Button>
          <Button data-testid="delete-play-btn" size="sm" variant="ghost" className="w-full h-8 text-destructive hover:text-destructive hover:bg-destructive/10" onClick={() => setConfirmDelete(true)}><Trash2 className="h-3.5 w-3.5 mr-1" />Delete play</Button>
          <Textarea data-testid="play-desc" value={play.description} onChange={(e) => setPlay({ ...play, description: e.target.value })} placeholder="Play description…" className="text-xs min-h-[60px]" />
          <NotesEditor play={play} setPlay={setPlay} />
          <AnnotationsEditor play={play} removeAnnotation={removeAnnotation} updateAnnotation={updateAnnotation} />
        </div>
    </div>
  );

  if (board) return <Whiteboard play={play} teamColors={teamColors} jersey={team?.jersey} onClose={() => setBoard(false)} />;

  return (
    <div className="h-screen w-screen flex flex-col bg-background overflow-hidden">
      {/* TOP BAR */}
      <div className="h-12 md:h-14 shrink-0 border-b border-border flex items-center gap-1.5 md:gap-2 px-2 md:px-3 bg-card/60 backdrop-blur">
        <Button data-testid="studio-back" variant="ghost" size="icon" className="h-8 w-8 md:h-9 md:w-9 shrink-0" onClick={() => nav(`/team/${play.teamId}`)}><ArrowLeft className="h-4 w-4" /></Button>
        <div className="flex items-center gap-2 min-w-0 flex-1 md:flex-none">
          <div className="hidden lg:flex h-8 w-8 rounded-md items-center justify-center font-display font-extrabold text-sm shrink-0" style={{ background: teamColors.primary + "22", color: teamColors.primary }}>{team?.name?.slice(0, 2).toUpperCase() || "GA"}</div>
          <Input data-testid="play-name-input" value={play.name} onChange={(e) => setPlay({ ...play, name: e.target.value })} className="h-8 w-full min-w-0 md:w-40 lg:w-56 font-display font-bold bg-transparent border-transparent hover:border-border focus:border-primary px-1 text-sm" />
        </div>
        <div className="hidden xl:flex items-center gap-1 ml-1">
          {["football", "hurling"].map((s) => (
            <button key={s} onClick={() => setPlay({ ...play, sport: s })} className={`text-[11px] uppercase tracking-wider px-2 py-1 rounded ${play.sport === s ? "bg-primary/15 text-primary" : "text-muted-foreground"}`}>{s}</button>
          ))}
        </div>

        <div className="md:mx-auto flex items-center gap-0.5 md:gap-1 bg-secondary rounded-lg p-0.5 md:p-1 shrink-0">
          {[{ id: "coach", label: "Coach", icon: Grid3x3 }, { id: "player", label: "Player", icon: User }, { id: "split", label: "Split", icon: Layout }].map((v) => (
            <button key={v.id} data-testid={`view-${v.id}`} onClick={() => setView(v.id)} title={v.label} aria-label={v.label} className={`flex items-center gap-1.5 px-2 lg:px-3 py-1.5 rounded-md text-xs font-semibold transition-colors ${view === v.id ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}>
              <v.icon className="h-3.5 w-3.5" /><span className="hidden lg:inline">{v.label}</span>
            </button>
          ))}
        </div>

        <div className="flex items-center gap-1 md:gap-1.5 shrink-0">
          <Button data-testid="board-btn" variant="ghost" size="sm" onClick={() => setBoard(true)} title="Full-screen whiteboard" className="h-8 px-2 md:px-2.5 text-[#F59E0B] hover:text-[#F59E0B]"><Presentation className="h-4 w-4 md:mr-1" /><span className="hidden md:inline">Board</span></Button>
          {isMobile && (
            <Button data-testid="inspector-toggle" variant={inspectorOpen ? "default" : "ghost"} size="icon" className="h-8 w-8" onClick={() => setInspectorOpen(true)} title="Inspector"><SlidersHorizontal className="h-4 w-4" /></Button>
          )}
          <Button data-testid="share-btn" variant="ghost" size="sm" onClick={share} className="hidden sm:flex"><Share2 className="h-4 w-4 mr-1" />Share</Button>
          <Button data-testid="save-btn" size="sm" onClick={save} disabled={saving} className="h-8 px-2.5 md:px-3"><Save className="h-4 w-4 md:mr-1" /><span className="hidden md:inline">{saving ? "…" : "Save"}</span></Button>
        </div>
      </div>

      {alignmentPending && (
        <div data-testid="play-alignment-notice" role="status" className="shrink-0 flex items-center justify-between gap-3 border-b border-primary/30 bg-primary/10 px-3 py-2 text-xs">
          <span>Older play aligned with your Starting 15. Runs and timing preserved.</span>
          <Button data-testid="save-alignment-btn" size="sm" onClick={save} disabled={saving} className="shrink-0 h-8">{saving ? "Saving…" : "Save alignment"}</Button>
        </div>
      )}

      {possessionIssue && (
        <div role="alert" data-testid="possession-warning" className="flex items-center gap-3 justify-between shrink-0 px-3 py-2 bg-amber-500/10 border-b border-amber-500/40 text-xs text-amber-200">
          <span>{possessionIssue.message}</span>
          <Button data-testid="review-possession-event" size="sm" variant="secondary" className="shrink-0" onClick={() => { setSelectedEventId(possessionIssue.id); if (isMobile) setInspectorOpen(true); }}>Review event</Button>
        </div>
      )}

      {/* MOBILE TOOL STRIP */}
      {isMobile && (
        <div className="h-12 shrink-0 border-b border-border bg-card/40 flex items-center gap-1 px-2 overflow-x-auto" data-testid="mobile-tool-strip">
          {TOOLS.map((tl) => (
            <button key={tl.id} data-testid={`tool-${tl.id}`} title={tl.label} onClick={() => { setTool(tl.id); setBallSource(null); }}
              className={`shrink-0 w-12 h-10 rounded-lg flex flex-col items-center justify-center gap-0.5 ${tool === tl.id ? "bg-primary text-primary-foreground tool-active" : "text-muted-foreground"}`}>
              <tl.icon className="h-4 w-4" />
              <span className="text-[8px] font-semibold uppercase leading-none">{tl.short}</span>
            </button>
          ))}
          <div className="w-px h-6 bg-border mx-1 shrink-0" />
          <button data-testid="tool-undo" onClick={undo} className="shrink-0 w-10 h-10 rounded-lg flex items-center justify-center text-muted-foreground"><Undo2 className="h-4 w-4" /></button>
          <button data-testid="tool-redo" onClick={redo} className="shrink-0 w-10 h-10 rounded-lg flex items-center justify-center text-muted-foreground"><Redo2 className="h-4 w-4" /></button>
          <button data-testid="tool-reset-formation" onClick={resetFormation} className="shrink-0 w-10 h-10 rounded-lg flex items-center justify-center text-muted-foreground"><LayoutGrid className="h-4 w-4" /></button>
        </div>
      )}

      {/* MIDDLE */}
      <div className="flex-1 flex min-h-0">
        {/* LEFT TOOLBAR */}
        {isMobile ? null : leftOpen ? (
          <div className="w-[76px] shrink-0 border-r border-border bg-card/40 flex flex-col items-center py-2 gap-1 overflow-y-auto">
            {TOOLS.map((tl) => (
              <button key={tl.id} data-testid={`tool-${tl.id}`} title={tl.label} onClick={() => { setTool(tl.id); setBallSource(null); }}
                className={`w-16 py-1.5 rounded-lg flex flex-col items-center gap-0.5 transition-all ${tool === tl.id ? "bg-primary text-primary-foreground tool-active" : "text-muted-foreground hover:bg-secondary hover:text-foreground"}`}>
                <tl.icon className="h-5 w-5" />
                <span className="text-[9px] font-semibold uppercase tracking-tight leading-none">{tl.short}</span>
              </button>
            ))}
            <div className="h-px w-10 bg-border my-1" />
            <button data-testid="tool-undo" title="Undo" onClick={undo} className="w-16 py-1.5 rounded-lg flex flex-col items-center gap-0.5 text-muted-foreground hover:bg-secondary hover:text-foreground"><Undo2 className="h-5 w-5" /><span className="text-[9px] font-semibold uppercase">Undo</span></button>
            <button data-testid="tool-redo" title="Redo" onClick={redo} className="w-16 py-1.5 rounded-lg flex flex-col items-center gap-0.5 text-muted-foreground hover:bg-secondary hover:text-foreground"><Redo2 className="h-5 w-5" /><span className="text-[9px] font-semibold uppercase">Redo</span></button>
            <button data-testid="tool-reset-formation" title="Reset formation" onClick={resetFormation} className="w-16 py-1.5 rounded-lg flex flex-col items-center gap-0.5 text-muted-foreground hover:bg-secondary hover:text-foreground"><LayoutGrid className="h-5 w-5" /><span className="text-[9px] font-semibold uppercase">Reset</span></button>
            <button title="Collapse" onClick={() => setLeftOpen(false)} className="w-16 py-1.5 rounded-lg flex flex-col items-center gap-0.5 text-muted-foreground hover:bg-secondary"><PanelLeftClose className="h-5 w-5" /><span className="text-[9px] uppercase">Hide</span></button>
          </div>
        ) : (
          <button onClick={() => setLeftOpen(true)} className="w-6 shrink-0 border-r border-border bg-card/40 flex items-center justify-center text-muted-foreground hover:text-foreground"><ChevronsRight className="h-4 w-4" /></button>
        )}

        {/* CANVAS */}
        <div className="flex-1 min-w-0 relative bg-[#08130b]">
          {view !== "player" && !playing && (
            <div className="absolute top-1 left-2 right-2 z-20 flex flex-col items-center gap-1 min-w-0">
              <div className="text-[10px] md:text-[11px] bg-background/85 backdrop-blur border border-border rounded-full px-3 py-1 text-muted-foreground text-center truncate max-w-full" data-testid="tool-hint">
                <span className="text-primary font-semibold uppercase tracking-wider mr-1.5">{tool}</span>{hint}
                {stage && <span className="ml-2 text-[#F59E0B] font-semibold">· {stage.name}</span>}
              </div>
              {runLike && (
                <div className="flex items-center gap-1.5 flex-wrap justify-center">
                  <div className="flex rounded-full border border-border bg-background/85 overflow-hidden" data-testid="pace-picker">
                    {PACES.map((pc) => (
                      <button key={pc.id} data-testid={`pace-${pc.id}`} onClick={() => setPace(pc.id)}
                        className={`text-[10px] uppercase tracking-wider px-2.5 py-0.5 ${pace === pc.id ? "bg-[#F59E0B] text-black font-bold" : "text-muted-foreground"}`}>{pc.label}</button>
                    ))}
                  </div>
                  <button data-testid="marker-follow-toggle" onClick={() => setMarkerFollow((v) => !v)}
                    className={`text-[10px] uppercase tracking-wider rounded-full px-2.5 py-0.5 border flex items-center gap-1 ${markerFollow ? "bg-[#EF4444]/15 border-[#EF4444]/60 text-[#fca5a5]" : "bg-background/80 border-border text-muted-foreground"}`}>
                    <Shield className="h-3 w-3" />Marker follows {markerFollow ? `· ${MARKER_DELAY}s later` : "off"}
                  </button>
                  <button data-testid="continuous-run-toggle" onClick={() => setContinuousRun((v) => !v)} title="A continuous run keeps animating across later stages so you can add moves that overlap it"
                    className={`text-[10px] uppercase tracking-wider rounded-full px-2.5 py-0.5 border flex items-center gap-1 ${continuousRun ? "bg-[#22C55E]/20 border-[#22C55E]/60 text-[#86efac]" : "bg-background/80 border-border text-muted-foreground"}`}>
                    <ChevronsRight className="h-3 w-3" />Continuous {continuousRun ? "on" : "off"}
                  </button>
                </div>
              )}
            </div>
          )}
          {banner && (
            <div key={banner.index + banner.name} data-testid="stage-banner" className="absolute bottom-14 left-2 z-30 pointer-events-none stage-banner max-w-[85%]">
              <div className="flex items-center gap-2 bg-[#0A0D14]/85 backdrop-blur border border-[#F59E0B]/60 rounded-lg px-3 py-1.5 shadow-lg">
                <span className="font-mono2 text-[10px] font-bold text-[#F59E0B] uppercase tracking-widest">Stage {banner.index + 1}</span>
                <span className="font-display font-bold text-xs text-foreground truncate">{banner.name}</span>
              </div>
            </div>
          )}
          {view === "coach" && (
            <div className="absolute inset-0 p-3" style={{ bottom: 32, top: playing ? 0 : runLike ? 60 : 32 }}>
              <OverheadPitch play={play} frame={frame} selectedId={selectedId} onSelect={onSelect} onMovePlayer={onMovePlayer} onMoveWaypoint={onMoveWaypoint} tool={tool} teamColors={teamColors} onPitchPoint={onPitchPoint} dragPreview={dragPreview} stageRange={stageRange} homeJersey={team?.jersey} />
            </div>
          )}
          {view === "player" && (
            <div className="absolute inset-0 bottom-8">
              <PlayerView {...pvProps} />
              <ViewAsHud players={homePlayers} focusId={focusId} setFocusId={setFocusId} compact={isMobile} />
            </div>
          )}
          {view === "split" && (
            <div className={`absolute inset-0 grid gap-1 p-1 ${isMobile ? "grid-rows-2" : "grid-cols-2"}`} style={{ bottom: 32, top: playing ? 0 : runLike ? 60 : 32 }}>
              <div className="relative rounded-lg overflow-hidden border border-border bg-[#08130b] min-h-0">
                <div className="absolute top-2 left-2 z-10 text-[10px] font-semibold uppercase tracking-widest bg-background/70 rounded px-2 py-0.5">Coach View</div>
                <OverheadPitch play={play} frame={frame} selectedId={selectedId} onSelect={onSelect} onMovePlayer={onMovePlayer} onMoveWaypoint={onMoveWaypoint} tool={tool} teamColors={teamColors} onPitchPoint={onPitchPoint} dragPreview={dragPreview} stageRange={stageRange} homeJersey={team?.jersey} compact />
              </div>
              <div className="relative rounded-lg overflow-hidden border border-border min-h-0">
                <PlayerView {...pvProps} />
                <ViewAsHud players={homePlayers} focusId={focusId} setFocusId={setFocusId} compact />
              </div>
            </div>
          )}

          {/* status chip */}
          <div className="absolute bottom-0 left-0 right-0 h-8 z-10 flex items-center justify-center gap-2 px-2 bg-background/95 border-t border-border font-mono2 text-[10px] md:text-xs whitespace-nowrap overflow-hidden" data-testid="status-chip">
            <span className="text-primary font-bold shrink-0">{t.toFixed(1)}s</span>
            <span className="text-muted-foreground shrink-0">/ {duration.toFixed(1)}s</span>
            {activeCarrier && <span className="text-[#FACC15] truncate">● {displayName(play, activeCarrier)} has the ball</span>}
            {frame.ball?.inFlight && <span className="text-[#38BDF8] truncate">Ball in flight</span>}
          </div>
          {isMobile && !playing && (selectedEntity || selectedEvent) && (
            <button data-testid="mobile-edit-selected" onClick={() => setInspectorOpen(true)} className="absolute bottom-10 right-3 z-10 h-9 px-3 rounded-full bg-primary text-primary-foreground text-xs font-semibold shadow-lg flex items-center gap-1.5">
              <SlidersHorizontal className="h-3.5 w-3.5" />{selectedEvent ? `Edit ${selectedEvent.type.toLowerCase()}` : `Edit #${selectedEntity.number}`}
            </button>
          )}
        </div>

        {/* RIGHT PANEL */}
        {isMobile ? null : rightOpen ? (
          <div className="w-72 shrink-0 border-l border-border bg-card/40 flex flex-col overflow-hidden">
            <div className="flex items-center justify-between px-4 h-11 border-b border-border">
              <span className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">Inspector</span>
              <button onClick={() => setRightOpen(false)} className="text-muted-foreground hover:text-foreground"><PanelRightClose className="h-4 w-4" /></button>
            </div>
            {inspectorBody}
          </div>
        ) : (
          <button onClick={() => setRightOpen(true)} className="w-6 shrink-0 border-l border-border bg-card/40 flex items-center justify-center text-muted-foreground hover:text-foreground"><ChevronsRight className="h-4 w-4 rotate-180" /></button>
        )}
      </div>

      {/* STAGES */}
      <StageBar stages={stages} activeStage={activeStage} t={t} onSelect={selectStage} onAdd={addStage} onPlayStage={playStage} onPlayAll={playAll} playing={playing} compact={isMobile} />

      {/* TIMELINE */}
      <Timeline
        play={play} t={t} setT={seekTo} duration={duration} playing={playing} stages={stages} compact={isMobile}
        setPlaying={setPlaying} speed={speed} setSpeed={setSpeed} loop={loop} setLoop={setLoop}
        onReset={() => { setT(0); setActiveStage(0); setPlaying(false); stopAtRef.current = null; }}
        onTogglePlay={togglePlay}
        selectedEventId={selectedEventId} onSelectEvent={(id) => { setSelectedEventId(id); setSelectedId(null); if (isMobile) setInspectorOpen(true); }}
      />

      <DeletePlayDialog open={confirmDelete} onOpenChange={setConfirmDelete} playName={play.name} onConfirm={deletePlay} busy={deleting} />

      {isMobile && (
        <Sheet open={inspectorOpen} onOpenChange={setInspectorOpen}>
          <SheetContent side="bottom" className="h-[72vh] p-0 flex flex-col rounded-t-2xl" data-testid="mobile-inspector">
            <div className="flex items-center justify-between px-4 h-11 border-b border-border shrink-0">
              <span className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">Inspector</span>
              <div className="flex items-center gap-1 mr-8">
                {["football", "hurling"].map((s) => (
                  <button key={s} onClick={() => setPlay({ ...play, sport: s })} className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded ${play.sport === s ? "bg-primary/15 text-primary" : "text-muted-foreground"}`}>{s}</button>
                ))}
              </div>
            </div>
            {inspectorBody}
          </SheetContent>
        </Sheet>
      )}
    </div>
  );
}

function StageBar({ stages, activeStage, t, onSelect, onAdd, onPlayStage, onPlayAll, playing, compact }) {
  return (
    <div className="h-11 shrink-0 border-t border-border bg-card/40 flex items-center gap-1.5 md:gap-2 px-2 md:px-3 overflow-x-auto" data-testid="stage-bar" style={{ scrollbarWidth: "none" }}>
      <Button data-testid="play-all-btn" size="sm" className="h-8 shrink-0 bg-[#F59E0B] hover:bg-[#d97706] text-black font-bold px-2.5" onClick={onPlayAll}><Play className="h-3.5 w-3.5 md:mr-1" /><span className="hidden md:inline">Play all</span></Button>
      {!compact && <div className="flex items-center gap-1 text-[10px] font-semibold uppercase tracking-widest text-muted-foreground shrink-0"><Layers className="h-3.5 w-3.5" />Stages</div>}
      {stages.map((s, i) => {
        const live = t >= s.start - 1e-6 && t < s.end - 1e-6;
        const prog = live ? Math.min(1, (t - s.start) / s.duration) : t >= s.end ? 1 : 0;
        const active = i === activeStage;
        return (
          <button key={s.id} data-testid={`stage-chip-${i}`} onClick={() => onSelect(i)} onDoubleClick={() => onPlayStage(i)}
            className={`relative shrink-0 h-8 ${compact ? "min-w-[84px] px-2" : "min-w-[110px] px-3"} rounded-md border text-left overflow-hidden transition-colors ${active ? "border-[#F59E0B] bg-[#F59E0B]/10 text-foreground" : "border-border text-muted-foreground hover:border-[#F59E0B]/50"}`}>
            <div className="absolute inset-y-0 left-0 bg-[#F59E0B]/15 pointer-events-none transition-[width]" style={{ width: `${prog * 100}%` }} />
            <div className="relative flex items-center gap-2">
              <span className={`font-mono2 text-[10px] font-bold ${active ? "text-[#F59E0B]" : ""}`}>{i + 1}</span>
              <span className="text-xs font-semibold truncate max-w-[120px]">{s.name}</span>
              <span className="font-mono2 text-[10px] opacity-60 ml-auto">{s.duration}s</span>
            </div>
          </button>
        );
      })}
      <Button data-testid="add-stage-btn" size="sm" variant="secondary" className="h-8 shrink-0 px-2.5" onClick={onAdd}><Plus className="h-3.5 w-3.5 md:mr-1" /><span className="hidden md:inline">Add stage</span></Button>
      <Button data-testid="play-stage-btn" size="sm" variant="ghost" className="h-8 shrink-0 text-[#F59E0B] hover:text-[#F59E0B] px-2" onClick={() => onPlayStage(activeStage)}><StepForward className="h-3.5 w-3.5 md:mr-1" /><span className="hidden md:inline">Play stage {activeStage + 1}</span><span className="md:hidden font-mono2 text-[10px] ml-0.5">{activeStage + 1}</span></Button>
    </div>
  );
}

function ViewAsHud({ players, focusId, setFocusId, compact }) {
  const hudRef = useRef(null);
  const [narrowView, setNarrowView] = useState(compact);
  useEffect(() => {
    const container = hudRef.current?.parentElement;
    if (!container) return;
    const observer = new ResizeObserver(([entry]) => setNarrowView(entry.contentRect.width < 700));
    observer.observe(container);
    return () => observer.disconnect();
  }, []);
  const cur = players.find((p) => p.id === focusId) || players[0];
  if (compact || narrowView) {
    return (
      <div ref={hudRef} className="absolute top-2 left-2 right-2 z-10 flex items-center gap-1.5 bg-background/80 backdrop-blur border border-primary/40 rounded-lg px-2 py-1" data-testid="view-as-hud">
        <div className="shrink-0 pr-2 border-r border-border">
          <div className="text-[8px] uppercase tracking-widest text-primary leading-none">Viewing</div>
          <div className="font-display font-bold text-xs leading-tight">#{cur?.number} {(cur?.name || "").split(" ")[0]}</div>
        </div>
        <div className="flex gap-1 overflow-x-auto min-w-0" style={{ scrollbarWidth: "none" }}>
          {players.map((p) => (
            <button key={p.id} data-testid={`hud-view-as-${p.id}`} onClick={() => setFocusId(p.id)}
              className={`shrink-0 font-mono2 text-[11px] h-7 min-w-7 px-1 rounded-md border ${cur?.id === p.id ? "border-primary bg-primary text-primary-foreground" : "border-border bg-background/70 text-muted-foreground"}`}>{p.number}</button>
          ))}
        </div>
      </div>
    );
  }
  return (
    <>
      <div ref={hudRef} className="absolute top-3 left-3 z-10 bg-background/80 backdrop-blur border border-primary/40 rounded-lg px-3 py-1.5" data-testid="view-as-hud">
        <div className="text-[10px] uppercase tracking-widest text-primary">Viewing as</div>
        <div className="font-display font-bold text-sm">#{cur?.number} {cur?.name}{cur?.position && <> · <span className="text-muted-foreground font-normal">{cur.position}</span></>}</div>
      </div>
      <div className="absolute top-3 right-3 z-10 flex flex-wrap gap-1 max-w-[62%] justify-end">
        {players.map((p) => (
          <button key={p.id} data-testid={`hud-view-as-${p.id}`} onClick={() => setFocusId(p.id)} title={p.name}
            className={`font-mono2 text-xs h-8 min-w-8 px-1.5 rounded-md border ${cur?.id === p.id ? "border-primary bg-primary text-primary-foreground" : "border-border bg-background/70 text-muted-foreground"}`}>{p.number}</button>
        ))}
      </div>
    </>
  );
}

function EntityInspector({ ent, updateEntity, deleteEntity, onViewAs }) {
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">{ent.team === "A" ? "Home player" : "Opposition"}</div>
        <button data-testid="delete-selected" onClick={() => deleteEntity(ent.id)} className="text-muted-foreground hover:text-destructive"><Trash2 className="h-4 w-4" /></button>
      </div>
      <div className="grid grid-cols-3 gap-2">
        <div>
          <label className="text-[11px] text-muted-foreground">Number</label>
          <Input data-testid="edit-number" type="number" value={ent.number} onChange={(e) => updateEntity(ent.id, { number: +e.target.value })} className="h-8" />
        </div>
        <div className="col-span-2">
          <label className="text-[11px] text-muted-foreground">Name</label>
          <Input data-testid="edit-name" value={ent.name} onChange={(e) => updateEntity(ent.id, { name: e.target.value })} className="h-8" />
        </div>
      </div>
      <div>
        <label className="text-[11px] text-muted-foreground">Position</label>
        <Input value={ent.position} onChange={(e) => updateEntity(ent.id, { position: e.target.value })} className="h-8" />
      </div>
      <div>
        <label className="text-[11px] text-muted-foreground">Role</label>
        <Input value={ent.role} onChange={(e) => updateEntity(ent.id, { role: e.target.value })} className="h-8" />
      </div>
      {ent.team === "A" && <Button data-testid="inspector-view-as" variant="secondary" className="w-full" onClick={onViewAs}><Eye className="h-4 w-4 mr-1" />View from #{ent.number}</Button>}
    </div>
  );
}

function EventInspector({ ev, play, duration, updateEvent, deleteEvent, onClose, onSetPace }) {
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="text-xs font-semibold uppercase tracking-widest text-primary">{ev.type}</div>
        <div className="flex gap-2">
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground"><X className="h-4 w-4" /></button>
          <button data-testid="delete-event" onClick={() => deleteEvent(ev.id)} className="text-muted-foreground hover:text-destructive"><Trash2 className="h-4 w-4" /></button>
        </div>
      </div>
      <div className="text-xs text-muted-foreground" data-testid="event-players"><span className="text-foreground">{displayName(play, ev.playerId)}</span>{ev.targetPlayerId ? <> → <span className="text-foreground">{displayName(play, ev.targetPlayerId)}</span></> : ev.target ? " → scoring attempt" : null}</div>
      {ev.kind === "ball" && (
        <div>
          <label htmlFor="event-destination" className="text-[11px] text-muted-foreground">Destination</label>
          <select id="event-destination" data-testid="event-destination" value={ev.target ? `shot-${ev.target.y === 0 ? "home" : "away"}` : ev.targetPlayerId || ""} onChange={(event) => {
            const destination = event.target.value;
            updateEvent(ev.id, destination.startsWith("shot-")
              ? { targetPlayerId: null, target: { x: 45, y: destination === "shot-home" ? 0 : 145, z: 4 }, ballType: "shot", type: "SHOT" }
              : { targetPlayerId: destination, target: null, ballType: ev.ballType === "shot" ? "kick" : ev.ballType, type: ev.ballType === "shot" ? "KICK" : ev.type });
          }} className="w-full mt-1 h-8 rounded-md border border-border bg-background text-xs px-2">
            {play.entities.filter((entity) => entity.id !== ev.playerId).map((entity) => <option key={entity.id} value={entity.id}>{displayName(play, entity.id)}</option>)}
            <option value="shot-away">Shot at opposition goal</option>
            <option value="shot-home">Shot at home goal</option>
          </select>
        </div>
      )}
      <div>
        <label className="text-[11px] text-muted-foreground">Start · {ev.startTime.toFixed(1)}s</label>
        <Slider value={[ev.startTime]} min={0} max={duration} step={0.1} onValueChange={(v) => updateEvent(ev.id, { startTime: round1(Math.min(v[0], ev.endTime - 0.1)) })} className="mt-2" />
      </div>
      <div>
        <label className="text-[11px] text-muted-foreground">End · {ev.endTime.toFixed(1)}s</label>
        <Slider value={[ev.endTime]} min={0} max={duration} step={0.1} onValueChange={(v) => updateEvent(ev.id, { endTime: round1(Math.max(v[0], ev.startTime + 0.1)) })} className="mt-2" />
      </div>
      {ev.kind === "move" && (
        <div>
          <label className="text-[11px] text-muted-foreground">Pace</label>
          <div className="flex rounded-md border border-border overflow-hidden mt-1 h-8" data-testid="event-pace-picker">
            {PACES.map((pc) => (
              <button key={pc.id} data-testid={`event-pace-${pc.id}`} onClick={() => onSetPace(ev, pc.id)}
                className={`flex-1 text-xs font-semibold ${(ev.pace || "run") === pc.id ? "bg-[#F59E0B] text-black" : "text-muted-foreground"}`}>{pc.label}</button>
            ))}
          </div>
        </div>
      )}
      {ev.note && <div className="text-xs text-muted-foreground italic">{ev.note}</div>}
    </div>
  );
}

function NotesEditor({ play, setPlay }) {
  const [val, setVal] = useState("");
  const notes = play.notes || [];
  return (
    <div>
      <div className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-2">Coaching notes</div>
      <div className="space-y-1.5 mb-2">
        {notes.map((n, i) => (
          <div key={i} className="flex items-start gap-2 text-xs bg-secondary/60 rounded px-2 py-1.5">
            <span className="flex-1">{n}</span>
            <button onClick={() => setPlay({ ...play, notes: notes.filter((_, j) => j !== i) })} className="text-muted-foreground hover:text-destructive"><X className="h-3 w-3" /></button>
          </div>
        ))}
      </div>
      <div className="flex gap-1.5">
        <Input data-testid="note-input" value={val} onChange={(e) => setVal(e.target.value)} placeholder="Add note…" className="h-8 text-xs" onKeyDown={(e) => { if (e.key === "Enter" && val.trim()) { setPlay({ ...play, notes: [...notes, val.trim()] }); setVal(""); } }} />
        <Button data-testid="add-note" size="icon" variant="secondary" className="h-8 w-8 shrink-0" onClick={() => { if (val.trim()) { setPlay({ ...play, notes: [...notes, val.trim()] }); setVal(""); } }}><Plus className="h-4 w-4" /></Button>
      </div>
    </div>
  );
}

function AnnotationsEditor({ play, removeAnnotation, updateAnnotation }) {
  const anns = play.annotations || [];
  const zones = anns.filter((a) => a.type === "zone");
  const others = anns.filter((a) => a.type !== "zone");
  if (!anns.length) return null;
  return (
    <div className="pt-2 border-t border-border">
      <div className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-2">Space &amp; markers</div>
      {zones.map((z) => (
        <div key={z.id} data-testid={`zone-item-${z.id}`} className="mb-2 bg-secondary/60 rounded-md p-2">
          <div className="flex items-center justify-between mb-1">
            <span className="text-xs font-semibold text-[#5EEAD4]">Space zone</span>
            <button data-testid={`zone-del-${z.id}`} onClick={() => removeAnnotation(z.id)} className="text-muted-foreground hover:text-destructive"><X className="h-3.5 w-3.5" /></button>
          </div>
          <label className="text-[10px] text-muted-foreground">Size · {z.r}m</label>
          <Slider value={[z.r]} min={5} max={30} step={1} onValueChange={(v) => updateAnnotation(z.id, { r: v[0] })} className="mt-1" />
        </div>
      ))}
      {others.map((a) => (
        <div key={a.id} className="flex items-center justify-between text-xs bg-secondary/60 rounded px-2 py-1.5 mb-1">
          <span className="capitalize">{a.type}{a.text ? `: ${a.text}` : ""}</span>
          <button onClick={() => removeAnnotation(a.id)} className="text-muted-foreground hover:text-destructive"><X className="h-3 w-3" /></button>
        </div>
      ))}
    </div>
  );
}

function Timeline({ play, t, setT, duration, playing, setPlaying, speed, setSpeed, loop, setLoop, selectedEventId, onSelectEvent, stages = [], onReset, onTogglePlay, compact }) {
  const trackRef = useRef(null);
  const dragging = useRef(false);

  const seek = (clientX) => {
    const rect = trackRef.current.getBoundingClientRect();
    const f = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    setT(round1(f * duration));
  };

  const step = duration <= 8 ? 1 : duration <= 16 ? 2 : duration <= 26 ? 3 : 5;
  const ticks = [];
  for (let s = 0; s <= duration + 0.001; s += step) {
    if (!compact || s === 0 || s === duration || (duration - s) / duration >= 0.12) ticks.push(Math.round(s));
  }
  if (ticks[ticks.length - 1] !== duration) ticks.push(duration);
  const laneEnds = [];
  const markers = [...(play.events || [])].filter((event) => !["SUPPORT", "COVER"].includes(event.type) || event.id === selectedEventId)
    .sort((a, b) => a.startTime - b.startTime || (a.kind === "ball" ? -1 : 1))
    .map((event) => {
      let lane = laneEnds.findIndex((end) => end + duration * 0.015 <= event.startTime);
      if (lane === -1) lane = laneEnds.length;
      laneEnds[lane] = event.endTime;
      return { event, lane };
    });
  const trackHeight = Math.max(38, laneEnds.length * 14 + 17);

  return (
    <div className={`${compact ? "gap-2 px-2" : "gap-3 px-3"} py-2 shrink-0 border-t border-border bg-card/60 backdrop-blur flex items-center`}>
      {/* transport */}
      <div className="flex items-center gap-1 md:gap-1.5">
        <Button data-testid="reset-btn" variant="ghost" size="icon" className="h-8 w-8 md:h-9 md:w-9" onClick={onReset}><RotateCcw className="h-4 w-4" /></Button>
        <Button data-testid="play-btn" size="icon" className="h-10 w-10 md:h-11 md:w-11 rounded-full" onClick={onTogglePlay}>
          {playing ? <Pause className="h-5 w-5" /> : <Play className="h-5 w-5 ml-0.5" />}
        </Button>
      </div>

      <div className="flex flex-col gap-1 min-w-0" style={{ width: compact ? 74 : 110 }}>
        <Select value={String(speed)} onValueChange={(v) => setSpeed(+v)}>
          <SelectTrigger data-testid="speed-select" className="h-8 text-xs px-2"><SelectValue /></SelectTrigger>
          <SelectContent>{SPEEDS.map((s) => <SelectItem key={s} value={String(s)}>{compact ? `${s}x` : `${s}x speed`}</SelectItem>)}</SelectContent>
        </Select>
        <button data-testid="loop-toggle" onClick={() => setLoop((l) => !l)} className={`text-[10px] uppercase tracking-wider rounded px-2 py-1 ${loop ? "bg-primary/15 text-primary" : "bg-secondary text-muted-foreground"}`}>Loop {loop ? "on" : "off"}</button>
      </div>

      {/* track */}
      <div className="flex-1 min-w-0">
        <select data-testid="timeline-event-picker" aria-label="Choose an event to edit" value={selectedEventId || ""} onChange={(event) => onSelectEvent(event.target.value || null)} className="mb-1 h-6 w-full max-w-md rounded border border-border bg-background text-[10px] px-1">
          <option value="">Edit a run, pass or off-ball movement…</option>
          {(play.events || []).map((event) => <option key={event.id} value={event.id}>{event.startTime.toFixed(1)}s · {event.type} · {displayName(play, event.playerId)}</option>)}
        </select>
        <div className="relative h-4 font-mono2 text-[10px] text-muted-foreground mb-1">
          {ticks.map((s) => <span data-testid={`timeline-tick-${s}`} key={s} className="absolute" style={{ left: `${s / duration * 100}%`, transform: s === 0 ? "none" : s === duration ? "translateX(-100%)" : "translateX(-50%)" }}>{s}s</span>)}
        </div>
        <div className="max-h-[78px] overflow-y-auto overflow-x-hidden rounded-lg">
        <div
          ref={trackRef}
          data-testid="timeline-track"
          className="relative rounded-lg bg-secondary/70 border border-border cursor-pointer"
          style={{ height: trackHeight }}
          onPointerDown={(e) => { dragging.current = true; seek(e.clientX); }}
          onPointerMove={(e) => { if (dragging.current) seek(e.clientX); }}
          onPointerUp={() => (dragging.current = false)}
          onPointerLeave={() => (dragging.current = false)}
        >
          {/* stage bands */}
          {stages.map((s, i) => (
            <div key={s.id} className="absolute top-0 bottom-0 pointer-events-none" style={{ left: `${(s.start / duration) * 100}%`, width: `${(s.duration / duration) * 100}%`, background: i % 2 ? "rgba(245,158,11,0.05)" : "transparent", borderLeft: i ? "1px dashed rgba(245,158,11,0.55)" : "none" }}>
              <span className="absolute bottom-0.5 left-1 text-[8px] font-mono2 uppercase text-[#F59E0B]/80 truncate max-w-full pr-1">{s.name}</span>
            </div>
          ))}
          {/* gridlines */}
          {ticks.map((s) => (<div key={s} className="absolute top-0 bottom-0 w-px bg-border/60" style={{ left: `${(s / duration) * 100}%` }} />))}
          {/* event markers */}
          {markers.map(({ event: ev, lane }) => {
            const color = ev.kind === "ball" ? EVENT_COLORS.ball : ev.playerId?.startsWith("B") ? EVENT_COLORS.opp : EVENT_COLORS.move;
            const left = (ev.startTime / duration) * 100;
            const width = ((ev.endTime - ev.startTime) / duration) * 100;
            const sel = ev.id === selectedEventId;
            return (
              <div key={ev.id} data-testid={`event-marker-${ev.id}`} onPointerDown={(e) => { e.stopPropagation(); onSelectEvent(ev.id); }}
                title={`${ev.type} · ${displayName(play, ev.playerId)} ${ev.startTime}-${ev.endTime}s`}
                className="absolute h-3 rounded-full cursor-pointer transition-opacity"
                style={{ top: 3 + lane * 14, left: `${left}%`, width: `max(8px, ${width}%)`, background: color, opacity: sel ? 1 : 0.75, boxShadow: sel ? `0 0 0 2px #00F0FF` : "none" }} />
            );
          })}
          {/* playhead */}
          <div className="absolute top-0 bottom-0 w-0.5 bg-[#00F0FF] pointer-events-none" style={{ left: `${(t / duration) * 100}%` }}>
            <div className="absolute -top-1 -left-[5px] h-3 w-3 rounded-full bg-[#00F0FF]" />
          </div>
        </div>
        </div>
      </div>
    </div>
  );
}
